// hero-4.twig

import React from 'react'
import {Section, Box, Video, Container} from '@miro-site/core'
import {
  contextualSpaces,
  Headline,
  Content,
  LazyImageAdapter,
  ButtonAdapter,
} from '@miro-site/components_utils'
import type {Assets} from '@miro-site/components_utils'
import {theme} from '@miro-site/design-tokens'

const BACKGROUNDS = {
  dark: theme.colors.primaryIndigo,
  blue: theme.colors.lightBlue,
  pink: theme.colors.lightPink,
  yellow: theme.colors.lightYellow,
  transparent: 'transparent',
}

interface HeroImage extends Assets {
  big?: boolean
}

interface VideoProps extends React.ComponentProps<typeof Video> {
  big?: boolean
}

interface HeroProps {
  headline: React.ComponentProps<typeof Headline>
  content?: React.ComponentProps<typeof Content>
  background?: keyof typeof BACKGROUNDS
  button: React.ComponentProps<typeof ButtonAdapter>
  additional_button?: React.ComponentProps<typeof ButtonAdapter>
  image?: HeroImage
  tooltip?: React.ComponentProps<typeof Content>
  video?: VideoProps
}

const ImageNormalStyles = {
  height: 'auto',
  display: 'block',
  margin: '0 auto',
  overflow: 'hidden',
  width: [437, 930],
  borderRadius: [4, 8],
  paddingLeft: [20, null, null, null],
  paddingRight: [20, null, null, null],
}

const VideoNormalStyles = {
  height: 'auto',
  display: 'block',
  margin: '0 auto',
  overflow: 'hidden',
  width: [325, 930],
  borderRadius: [4, 8],
}

const VideoBigStyles = {
  ...VideoNormalStyles,
  width: [325, 1120],
}

const ImageBigStyles = {
  ...ImageNormalStyles,
  width: [437, 1120],
}
const Hero: React.FC<HeroProps> = ({
  background,
  headline,
  content,
  button,
  additional_button,
  image,
  tooltip,
  video,
}) => {
  const isDarkMode = background == 'dark'

  const getAdditionalButtonType = () => {
    if (additional_button?.type) return additional_button.type

    return background ? 'white' : 'transparent'
  }

  return (
    <Section
      data-place="Hero Section"
      type="large"
      $styles={{backgroundColor: background ? BACKGROUNDS[background] : null, textAlign: 'center'}}
    >
      <Container>
        {headline?.text && (
          <Headline
            {...headline}
            color={isDarkMode ? 'white' : headline.color}
            size="medium"
            tag="h1"
            align={headline.align || 'center'}
          />
        )}
        {content && (
          <Content
            {...content}
            textProps={{
              $mx: 'auto',
              $styles: {
                maxWidth: [null, 550],
                marginTop: contextualSpaces.textSpaces.afterHeadline.medium,
              },
            }}
            color={isDarkMode ? 'white' : content.color}
            colorTransparency={isDarkMode ? 0.4 : null}
          ></Content>
        )}
        {(button || additional_button) && (
          <Box $styles={{marginTop: contextualSpaces.buttonSpaces.afterText.common}}>
            <ButtonAdapter {...button}></ButtonAdapter>
            {additional_button && (
              <ButtonAdapter
                $styles={{marginLeft: [theme.spaces.md, theme.spaces.sm]}}
                {...additional_button}
                type={getAdditionalButtonType()}
              ></ButtonAdapter>
            )}
          </Box>
        )}
        {tooltip && (
          <Content
            {...tooltip}
            textProps={{$styles: {marginTop: [theme.spaces.md]}}}
            size="tiny"
            color={isDarkMode ? 'white' : tooltip.color}
            colorTransparency={isDarkMode ? 0.7 : null}
          ></Content>
        )}
        {image && (
          <Box
            $styles={{position: 'relative', overflowX: 'auto'}}
            $mt={[40, 80]}
            $mx={[-20, null, null, null]}
          >
            <Box $styles={image.big ? ImageBigStyles : ImageNormalStyles}>
              <LazyImageAdapter assets={image}></LazyImageAdapter>
            </Box>
          </Box>
        )}

        {video?.id && (
          <Box $styles={{position: 'relative'}} $mt={[40, 80]}>
            <Box $styles={video.big ? VideoBigStyles : VideoNormalStyles}>
              <Video id={video.id} thumbnail={video.thumbnail}></Video>
            </Box>
          </Box>
        )}
      </Container>
    </Section>
  )
}

export default Hero
